import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import SafeImage from '../utils/SafeImage';
import { useLocalStorage } from '../utils/useLocalStorage';
import { useSharedNotes } from '../hooks/useSharedNotes';

// Maps activity type → badge classes
const TYPE_BADGE = {
    food:          'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    accommodation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    transport:     'bg-blue-100  text-blue-800  dark:bg-blue-900/30  dark:text-blue-300',
    shopping:      'bg-pink-100  text-pink-800  dark:bg-pink-900/30  dark:text-pink-300',
};
const defaultBadge = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';

function getTypeBadge(type) { return TYPE_BADGE[type] ?? defaultBadge; }

// ─── Sub-components ──────────────────────────────────────────────────────────

function HotelCard({ hotel }) {
    const { t } = useTranslation();
    return (
        <div className="card p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5 flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-gray-100">🏨 {hotel.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{hotel.address}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        {hotel.rating !== 0 && (
                            <span className="badge bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                ⭐ {hotel.rating}
                            </span>
                        )}
                        {hotel.price !== 0 && (
                            <span className="font-bold text-primary-700 dark:text-primary-400">{t('cityDetails.hotel.perNight', { price: hotel.price })}</span>
                        )}
                    </div>
                    {hotel.notes && <p className="text-sm italic text-gray-600 dark:text-gray-400">{hotel.notes}</p>}
                </div>

                <div className="flex flex-col gap-2">
                    {hotel.bookingLink && (
                        <a href={hotel.bookingLink} target="_blank" rel="noopener noreferrer"
                           className="btn-primary text-sm text-center">
                            📅 Booking
                        </a>
                    )}
                    {hotel.mapsLink && (
                        <a href={hotel.mapsLink} target="_blank" rel="noopener noreferrer"
                           className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors duration-200 text-center">
                            🗺️ Maps
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function TransportCard({ activity, currency, noteKey, personalNotes, onNoteChange, sharedNotes, onAddShared, onDeleteShared }) {
    const { t } = useTranslation();
    return (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <span className="text-2xl">🚄</span>
                    <div className="flex flex-col gap-1 flex-1">
                        <p className="font-bold text-gray-900 dark:text-gray-100">
                            {activity.from} → {activity.to}
                        </p>
                        {activity.distance !== 0 && <p className="text-sm text-gray-700 dark:text-gray-300">{t('cityDetails.transport.distance', { value: activity.distance })}</p>}
                        {activity.timeS   && <p className="text-sm text-gray-700 dark:text-gray-300">{t('cityDetails.transport.departure', { time: activity.timeS })}</p>}
                        {activity.timeE   && <p className="text-sm text-gray-700 dark:text-gray-300">{t('cityDetails.transport.arrival', { time: activity.timeE })}</p>}
                        {activity.name    && <p className="text-sm italic text-gray-600 dark:text-gray-400">{activity.name}</p>}
                        {activity.notes   && <p className="text-sm italic text-gray-600 dark:text-gray-400">{activity.notes}</p>}
                        {activity.link && (
                            <a href={activity.link} target="_blank" rel="noopener noreferrer"
                               className="self-start mt-1 px-2.5 py-1 rounded-lg border border-blue-500 text-blue-700 dark:text-blue-300 text-xs font-semibold hover:bg-blue-500 hover:text-white transition-all duration-200">
                                {t('common.link')}
                            </a>
                        )}
                        <NoteField noteKey={noteKey} personalNotes={personalNotes} onNoteChange={onNoteChange} />
                        <SharedNotesSection activityKey={noteKey} sharedNotes={sharedNotes} onAdd={onAddShared} onDelete={onDeleteShared} />
                    </div>
                </div>
                {activity.cost !== 0 && (
                    <span className="badge bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-sm font-bold">
                        {currency}{activity.cost}
                    </span>
                )}
            </div>
        </div>
    );
}

function NoteField({ noteKey, personalNotes, onNoteChange }) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const current = personalNotes?.[noteKey] || '';

    return (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 no-print">
            {editing ? (
                <textarea
                    value={current}
                    onChange={e => onNoteChange(noteKey, e.target.value)}
                    onBlur={() => setEditing(false)}
                    rows={2}
                    autoFocus
                    placeholder={t('cityDetails.notes.personal')}
                    className="w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
                />
            ) : (
                <div className="flex items-start gap-2">
                    {current && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 italic flex-1">{current}</p>
                    )}
                    <button
                        onClick={() => setEditing(true)}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 whitespace-nowrap transition-colors"
                    >
                        {current ? t('cityDetails.notes.edit') : t('cityDetails.notes.add')}
                    </button>
                </div>
            )}
        </div>
    );
}

function SharedNotesBottomSheet({ activityKey, sharedNotes, onAdd, onDelete, onClose }) {
    const { t } = useTranslation();
    const [text, setText]             = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [author, setAuthor]         = useLocalStorage('shared_notes_author', '');

    const notes = sharedNotes ?? [];

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setSubmitting(true);
        try {
            await onAdd(activityKey, text.trim(), author.trim());
            setText('');
        } catch {}
        finally { setSubmitting(false); }
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Sheet */}
            <div className="relative bg-white dark:bg-gray-800 rounded-t-2xl max-h-[70vh] flex flex-col shadow-2xl">
                {/* Handle */}
                <div className="flex justify-center pt-3 pb-1 shrink-0">
                    <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 shrink-0">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('cityDetails.sharedNotes.title')}</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none"
                    >✕</button>
                </div>

                {/* Notes list — scrollable */}
                <div className="overflow-y-auto flex-1 px-4 py-3 flex flex-col gap-2">
                    {notes.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                            {t('cityDetails.sharedNotes.empty')}
                        </p>
                    )}
                    {notes.map(n => (
                        <div key={n.id} className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg px-3 py-2 flex items-start justify-between gap-2">
                            <div className="flex flex-col gap-0.5 flex-1">
                                <p className="text-sm text-indigo-900 dark:text-indigo-200">{n.content}</p>
                                <p className="text-xs text-indigo-400 dark:text-indigo-500">
                                    — {n.author} · {new Date(n.createdAt).toLocaleDateString(
                                        typeof window !== 'undefined' && localStorage.getItem('lang') === 'en' ? 'en-US' : 'it-IT'
                                    )}
                                </p>
                            </div>
                            <button
                                onClick={() => onDelete(n.id, activityKey)}
                                className="text-xs text-gray-400 hover:text-red-500 transition-colors shrink-0 mt-0.5"
                                title={t('common.delete')}
                            >✕</button>
                        </div>
                    ))}
                </div>

                {/* Add note form — always visible at bottom */}
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 shrink-0">
                    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={author}
                                onChange={e => setAuthor(e.target.value)}
                                placeholder={t('cityDetails.sharedNotes.namePlaceholder')}
                                className="w-28 text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            <textarea
                                value={text}
                                onChange={e => setText(e.target.value)}
                                rows={2}
                                placeholder={t('cityDetails.sharedNotes.textPlaceholder')}
                                className="flex-1 text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting || !text.trim()}
                            className="self-end px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-colors"
                        >
                            {submitting ? t('cityDetails.sharedNotes.submitting') : t('cityDetails.sharedNotes.submit')}
                        </button>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
}

function SharedNotesSection({ activityKey, sharedNotes, onAdd, onDelete }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const notes = sharedNotes ?? [];

    return (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 no-print">
            <button
                onClick={() => setOpen(true)}
                className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 transition-colors font-medium"
            >
                {notes.length > 0
                    ? t('cityDetails.sharedNotes.button', { count: notes.length })
                    : t('cityDetails.sharedNotes.buttonEmpty')}
                <span className="text-gray-400">▶</span>
            </button>

            {open && (
                <SharedNotesBottomSheet
                    activityKey={activityKey}
                    sharedNotes={notes}
                    onAdd={onAdd}
                    onDelete={onDelete}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    );
}

function ActivityCard({ activity, currency, noteKey, personalNotes, onNoteChange, sharedNotes, onAddShared, onDeleteShared }) {
    const { t } = useTranslation();
    return (
        <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors duration-200">
            <div className="flex items-start gap-4">
                {activity.image && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <SafeImage src={activity.image} alt={activity.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                )}
                <div className="flex flex-col gap-1 flex-1">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            {activity.time && (
                                <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{activity.time}</span>
                            )}
                            <span className={`badge ${getTypeBadge(activity.type)}`}>{activity.type}</span>
                        </div>
                        {activity.cost !== 0 && (
                            <span className="font-bold text-primary-700 dark:text-primary-400">{currency}{activity.cost}</span>
                        )}
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{activity.name}</p>
                    {activity.notes && <p className="text-sm text-gray-600 dark:text-gray-400">{activity.notes}</p>}
                    {activity.link && (
                        <a href={activity.link} target="_blank" rel="noopener noreferrer"
                           className="self-start mt-1 px-2.5 py-1 rounded-lg border border-primary-500 text-primary-700 dark:text-primary-400 text-xs font-semibold hover:bg-primary-600 hover:text-white transition-all duration-200">
                            {t('common.link')}
                        </a>
                    )}
                    <NoteField noteKey={noteKey} personalNotes={personalNotes} onNoteChange={onNoteChange} />
                    <SharedNotesSection activityKey={noteKey} sharedNotes={sharedNotes} onAdd={onAddShared} onDelete={onDeleteShared} />
                </div>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

function groupActivitiesByOption(activities) {
    return activities.reduce(
        (acc, activity) => {
            if (activity.multipleOption) {
                const opt = activity.multipleOption;
                acc.options[opt] = [...(acc.options[opt] || []), activity];
            } else {
                acc.regular.push(activity);
            }
            return acc;
        },
        { regular: [], options: {} }
    );
}

export default function CityDetails({ city, trip, token, onBack }) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'en' ? 'en-US' : 'it-IT';
    const [selectedOptions, setSelectedOptions] = useState({});
    const currency = trip?.currency || '€';

    const notesStorageKey = `personal_notes_${trip?.id || encodeURIComponent(trip?.name || 'trip')}`;
    const [personalNotes, setPersonalNotes] = useLocalStorage(notesStorageKey, {});
    const updateNote = (key, text) =>
        setPersonalNotes(prev => ({ ...prev, [key]: text }));

    const { notes: sharedNotes, addNote: addSharedNote, deleteNote: deleteSharedNote } =
        useSharedNotes(trip?.id, token);

    const toggleOption = (dayIndex, option) =>
        setSelectedOptions(prev => ({ ...prev, [dayIndex]: option }));

    const getActivitiesToShow = (day, dayIndex) => {
        const { regular, options } = groupActivitiesByOption(day.activities);
        const selected = selectedOptions[dayIndex];
        if (selected && options[selected]) return options[selected];
        if (Object.keys(options).length > 0) return regular;
        return day.activities;
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col gap-6">
            <button onClick={onBack} className="btn-outline self-start text-sm">
                {t('cityDetails.backToCities')}
            </button>

            {/* City header */}
            <div className="bg-gradient-to-br from-primary-50 via-white to-purple-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800/60 rounded-2xl p-6 border border-primary-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{city.name}</h2>
                {city.hotel && <HotelCard hotel={city.hotel} />}
            </div>

            {/* Days */}
            {city.days.map((day, idx) => {
                const { options } = groupActivitiesByOption(day.activities);
                const hasOptions = Object.keys(options).length > 0;
                const activitiesToShow = getActivitiesToShow(day, idx);

                return (
                    <div key={idx} className="card p-6 flex flex-col gap-4">
                        <h3 className="text-lg font-bold text-primary-700 dark:text-primary-400">
                            {new Date(day.date).toLocaleDateString(locale, {
                                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                            })} — {t('cityDetails.day', { number: idx + 1 })}
                        </h3>

                        {hasOptions && (
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('cityDetails.selectOption')}</p>
                                <div className="flex flex-wrap gap-2">
                                    {Object.keys(options).map(opt => (
                                        <button
                                            key={opt}
                                            onClick={() => toggleOption(idx, opt)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                                selectedOptions[idx] === opt
                                                    ? 'bg-primary-600 text-white'
                                                    : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-500'
                                            }`}
                                        >
                                            {t('cityDetails.option', { name: opt })}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col gap-3">
                            {activitiesToShow.map((activity, i) => {
                                const noteKey = `${city.name}_${idx}_${i}`;
                                const sharedProps = {
                                    sharedNotes:    sharedNotes[noteKey],
                                    onAddShared:    addSharedNote,
                                    onDeleteShared: deleteSharedNote,
                                };
                                return activity.type === 'transport' && activity.from
                                    ? <TransportCard key={i} activity={activity} currency={currency}
                                                     noteKey={noteKey} personalNotes={personalNotes} onNoteChange={updateNote}
                                                     {...sharedProps} />
                                    : <ActivityCard   key={i} activity={activity} currency={currency}
                                                     noteKey={noteKey} personalNotes={personalNotes} onNoteChange={updateNote}
                                                     {...sharedProps} />;
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
